import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '../api/chatApi';
import type {
  Conversation,
  ConversationVisibility,
  CreateConversationInput,
  UpdateConversationInput,
} from '../types';

export const CHAT_KEYS = {
  all: (organizationId: string) => ['chat', 'conversations', organizationId] as const,
  list: (organizationId: string, includeArchived: boolean) =>
    ['chat', 'conversations', organizationId, { includeArchived }] as const,
  one: (organizationId: string, conversationId: string) =>
    ['chat', 'conversations', organizationId, conversationId] as const,
  messages: (organizationId: string, conversationId: string) =>
    ['chat', 'conversations', organizationId, conversationId, 'messages'] as const,
};

export function useConversations(organizationId: string | undefined, includeArchived = false) {
  return useQuery({
    queryKey: organizationId
      ? CHAT_KEYS.list(organizationId, includeArchived)
      : ['chat', 'conversations', 'no-org'],
    queryFn: () => chatApi.list(organizationId as string, includeArchived),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
  });
}

export function useConversationMessages(
  organizationId: string | undefined,
  conversationId: string | null,
) {
  return useQuery({
    queryKey:
      organizationId && conversationId
        ? CHAT_KEYS.messages(organizationId, conversationId)
        : ['chat', 'messages', 'pending'],
    queryFn: () => chatApi.getMessages(conversationId as string, organizationId as string),
    enabled: !!organizationId && !!conversationId,
    // Don't refetch on focus — once we've loaded a conversation, the
    // SSE stream will append new messages, and the orchestrator's
    // onMessageStop pushes the assistant reply into the cache directly.
    refetchOnWindowFocus: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useConversation(organizationId: string | undefined, conversationId: string | null) {
  return useQuery({
    queryKey:
      organizationId && conversationId
        ? CHAT_KEYS.one(organizationId, conversationId)
        : ['chat', 'conversations', 'pending'],
    queryFn: () => chatApi.get(conversationId as string, organizationId as string),
    enabled: !!organizationId && !!conversationId,
    staleTime: 10_000,
  });
}

export function useCreateConversation(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateConversationInput) => chatApi.create(input, organizationId as string),
    onSuccess: (created) => {
      if (!organizationId) return;
      queryClient.setQueryData<Conversation[]>(CHAT_KEYS.list(organizationId, false), (prev) => [
        created,
        ...(prev ?? []),
      ]);
      queryClient.invalidateQueries({ queryKey: CHAT_KEYS.all(organizationId) });
    },
  });
}

export function useUpdateConversation(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateConversationInput }) =>
      chatApi.update(id, input, organizationId as string),
    onSuccess: () => {
      if (!organizationId) return;
      queryClient.invalidateQueries({ queryKey: CHAT_KEYS.all(organizationId) });
    },
  });
}

export function useUpdateVisibility(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: ConversationVisibility }) =>
      chatApi.updateVisibility(id, visibility, organizationId as string),
    onSuccess: () => {
      if (!organizationId) return;
      queryClient.invalidateQueries({ queryKey: CHAT_KEYS.all(organizationId) });
    },
  });
}

export function useArchiveConversation(organizationId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => chatApi.archive(id, organizationId as string),
    onSuccess: () => {
      if (!organizationId) return;
      queryClient.invalidateQueries({ queryKey: CHAT_KEYS.all(organizationId) });
    },
  });
}
